import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { PortalOrdersBoard, AdListBoard, AdSizesBoard } from '@api/BoardSDK';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { dealByKey, addMonths, PAY_CHOICES, priceFor } from '@generated/components/dealCatalog';

const GEORGE_ID = '106410322';
const PAPERS = ['Red Hook Star-Revue', 'The Village Star-Revue'] as const;

/** A reader claims a special deal: saves the order, puts every issue on the Ad List, pings George. */
export const claimDeal = createServerFn({ method: 'POST' })
  .validator(z.object({
    key: z.string().min(1).max(60),
    paper: z.enum(['Red Hook Star-Revue', 'The Village Star-Revue', 'Both']).optional(),
    adSize: z.string().max(80).optional(),
    startMonth: z.string().regex(/^[A-Z][a-z]+ \d{4}$/).optional(),
    business: z.string().trim().min(1).max(120),
    contact: z.string().trim().min(1).max(120),
    email: z.string().trim().max(200).optional(),
    phone: z.string().trim().max(40).optional(),
    artwork: z.enum(['upload', 'design']),
    method: z.enum(PAY_CHOICES),
    notes: z.string().trim().max(2000).optional(),
  }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    const base = dealByKey(data.key);
    if (!base) throw new Error('That deal is no longer available. Please call 917-652-9128.');
    let deal = base;
    if (base.sizeFactor) {
      const sizes = await new AdSizesBoard().items().withColumns(['pricePerIssue']).withPagination({ limit: 50 }).execute();
      const s = sizes.items.find((i) => i.name === data.adSize);
      if (!s?.pricePerIssue) throw new Error('Please choose an ad size. Questions? Call 917-652-9128.');
      deal = { ...base, adSize: s.name, total: Math.round(s.pricePerIssue * base.sizeFactor * 100) / 100 };
    }
    const papers: (typeof PAPERS)[number][] = deal.papers === 'both' || (deal.papers === 'choose' && data.paper === 'Both') ? [...PAPERS]
      : deal.papers === 'choose' ? [data.paper === 'The Village Star-Revue' ? 'The Village Star-Revue' : 'Red Hook Star-Revue'] : [deal.papers];
    const start = deal.fixedStart ?? data.startMonth;
    if (!start) throw new Error('Please choose a start month.');
    const months = Array.from({ length: deal.issues }, (_, i) => addMonths(start, i));
    deal = { ...deal, total: priceFor(deal.total, deal, papers.length === 2) };
    const pubLabel = papers.length === 2 ? 'Both papers (bundle)' : papers[0];
    const payLabel = data.method === 'Bill me later' ? 'Bill me later (invoice)' : data.method;
    const artLabel = data.artwork === 'upload' ? 'Advertiser is uploading their ad' : 'Please design the ad for us';

    const orders = new PortalOrdersBoard();
    const order = await orders.item().create({
      name: data.business,
      status: 'New Order',
      orderDate: new Date(),
      totalContractValue: deal.total,
      monthlyRate: Math.round((deal.total / deal.issues) * 100) / 100,
      paymentMethod: payLabel,
      contactPerson: data.contact,
      email: data.email ? { email: data.email, label: data.email } : null,
      phone: data.phone ? { phone: data.phone, country: 'US' } : null,
      publication: pubLabel,
      adSize: deal.adSize,
      duration: `${deal.issues} ${deal.issues === 1 ? 'month' : 'months'}`,
      startMonth: start,
      specialInstructions: [`SPECIAL DEAL: ${deal.name} ($${deal.total} total)`, `Artwork: ${artLabel}`, `Payment: ${payLabel}`, data.notes ? `Notes: ${data.notes}` : ''].filter(Boolean).join('\n'),
    }).execute();

    // Put each issue on the monthly Ad List right away.
    const cost = Math.round((deal.total / deal.issues / papers.length) * 100) / 100;
    const adList = new AdListBoard();
    let added = 0;
    try {
      for (const [i, m] of months.entries()) for (const paper of papers) {
        await adList.item().create({
          name: data.business, issueMonth: m, paper, adSize: deal.adSize, cost,
          section: deal.section ?? 'ROP', specialSectionName: deal.sectionName ?? '',
          adCopy: data.artwork === 'upload' ? (i === 0 ? 'Have Copy' : 'Same as Last Month') : 'Need Copy',
          billed: 'Not Billed', portalOrderId: order.id,
          notes: `${deal.name} · ${payLabel}${data.artwork === 'design' ? ' · We design the ad' : ''}`,
        }).execute();
        added++;
      }
      await orders.item(order.id).update({ status: 'Copied to Monthly List' }).execute();
    } catch (e) { console.error('[claimDeal] ad list failed', e); }

    try {
      await orders.item(order.id).notify(GEORGE_ID).create(`Deal claimed: ${deal.name} for ${data.business} (${pubLabel}, from ${start}) · ${payLabel} · ${artLabel}`).execute();
    } catch (e) { console.error('[claimDeal] notify failed', e); }

    return { id: order.id, total: deal.total, months, papers, added };
  }));

/** Public: one-time price per ad size (for size-based deals). */
export const getAdSizePrices = createServerFn({ method: 'POST' })
  .validator(z.object({}).optional())
  .handler(async () => withBoardAuth(async () => {
    const r = await new AdSizesBoard().items().withColumns(['pricePerIssue']).withPagination({ limit: 50 }).execute();
    return r.items.filter((i) => (i.pricePerIssue ?? 0) > 0).map((i) => ({ name: i.name, price: i.pricePerIssue ?? 0 })).sort((a, b) => b.price - a.price);
  }));
